import { Moment } from "moment-timezone";
import { useCallback, useMemo } from "react";
import { matchRoutes } from "react-router";
import { useIntl } from "react-intl";
import isoCountries from "i18n-iso-countries";
import { AnyPeriod, castPeriod } from "@ct-react/core";
import { useLocaleContext, useLocaleUrl } from "@ct-react/locale";
import { pricing } from "../i18n/sharable-defs";
import { routesConfig } from "../routes";

export const useTools = () => {

  const intl = useIntl();
  const { locale } = useLocaleContext();
  const { currentUrl } = useLocaleUrl();

  const routingData = useMemo(() => (key: "search"): any | undefined => {
    const matches = matchRoutes(routesConfig(), currentUrl, /(\/[^\/]*)\//.exec(currentUrl)![1])
      ?.filter(r => !!r.route.data && key in r.route.data);
    if (!matches) return undefined;
    return matches.reduce((acc, cur) => ({...acc, ...cur.route.data![key]}), {});
  }, [ currentUrl ]);

  const nights = useCallback((period: AnyPeriod) => {
    const casted = castPeriod(period);
    return casted.end.diff(casted.start, "day");
  }, []);

  const countries = useMemo(() => Object.fromEntries(Object
    .entries(isoCountries.getNames(locale.basename, { select: "official" }))
    .sort((a, b) => a[1].localeCompare(b[1]))), [ locale ]);

  const labelizeCalendar = useMemo(() => ({
    month: (m: Moment) => intl.formatDate(m.toDate(), { month: "long", year: "numeric" }),
    dayOfWeek: (d: Moment) => intl.formatDate(d.toDate(), { weekday: "short" }),
    onlineRestricted: intl.formatMessage(pricing.onDemandShort)
  }), [ intl ]);

  return {
    routingData,
    format: { nights },
    countries,
    labelizeCalendar
  };

}
